import { useCallback, useEffect, useState } from 'react';
import { useAuthStore, type AuthUser, type UserRole } from '../../store/useAuthStore';
import { amplifyConfig } from './amplify-config';

/**
 * useAuth — thin hook over Amplify's auth methods, mirrored into the Zustand
 * auth store so non-React code (api/client.ts) can read tokens synchronously.
 *
 * Why the dynamic imports: aws-amplify/auth ships a large CSP-safe runtime;
 * the login + callback pages are the only places that need it. Pulling it
 * lazily here keeps the main app bundle small (see vite chunk analysis).
 */

export interface CognitoIdTokenClaims {
  sub: string;
  email?: string;
  'custom:tenant_id'?: string;
  'custom:role'?: string;
  [key: string]: unknown;
}

async function ensureAmplifyConfigured(): Promise<void> {
  const { Amplify } = await import('aws-amplify');
  Amplify.configure(amplifyConfig);
}

export function useAuth() {
  const store = useAuthStore();
  const setLoading = store.setLoading;
  const signInToStore = store.signIn;
  const signOutOfStore = store.signOut;

  const signIn = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await ensureAmplifyConfigured();
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect();
    } catch (err) {
      setLoading(false);
      throw err;
    }
  }, [setLoading]);

  const signOut = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await ensureAmplifyConfigured();
      const { signOut: amplifySignOut } = await import('aws-amplify/auth');
      await amplifySignOut();
    } finally {
      signOutOfStore();
    }
  }, [setLoading, signOutOfStore]);

  const handleRedirectCallback = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await ensureAmplifyConfigured();
      const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth');
      const [session, current] = await Promise.all([
        fetchAuthSession({ forceRefresh: true }),
        getCurrentUser(),
      ]);
      const idToken = session.tokens?.idToken?.toString();
      const accessToken = session.tokens?.accessToken?.toString();
      if (!idToken || !accessToken) {
        throw new Error('Cognito did not return both id and access tokens');
      }
      const claims = (session.tokens?.idToken?.payload ?? {}) as CognitoIdTokenClaims;
      const user: AuthUser = {
        userId: current.userId,
        email: typeof claims.email === 'string' ? claims.email : current.signInDetails?.loginId ?? '',
        tenantId: typeof claims['custom:tenant_id'] === 'string' ? claims['custom:tenant_id'] : '',
        role: normalizeRole(claims['custom:role']),
      };
      signInToStore(user, {
        idToken,
        accessToken,
        // Amplify manages refresh internally on Hosted-UI sessions; we don't
        // get the raw refresh token here. The auth store keeps a slot for it
        // so the password-flow path (login.tsx) can drive manual refresh.
        refreshToken: '',
        expiresAt: expiresAtFromClaims(claims),
      });
    } catch (err) {
      signOutOfStore();
      throw err;
    }
  }, [setLoading, signInToStore, signOutOfStore]);

  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    signIn,
    signOut,
    handleRedirectCallback,
  };
}

/**
 * Rehydrate Amplify -> Zustand on app boot. Returns true once the in-flight
 * session check resolves. Useful for splash gating in a future PR; today the
 * router relies on store.isAuthenticated which starts false and flips when
 * fetchAuthSession succeeds.
 */
export function useAuthBootstrap(): boolean {
  const [ready, setReady] = useState(false);
  const signInToStore = useAuthStore((s) => s.signIn);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureAmplifyConfigured();
        const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const accessToken = session.tokens?.accessToken?.toString();
        if (!idToken || !accessToken) return;
        const current = await getCurrentUser();
        const claims = (session.tokens?.idToken?.payload ?? {}) as CognitoIdTokenClaims;
        if (cancelled) return;
        signInToStore(
          {
            userId: current.userId,
            email: typeof claims.email === 'string' ? claims.email : current.signInDetails?.loginId ?? '',
            tenantId:
              typeof claims['custom:tenant_id'] === 'string' ? claims['custom:tenant_id'] : '',
            role: normalizeRole(claims['custom:role']),
          },
          {
            idToken,
            accessToken,
            refreshToken: '',
            expiresAt: expiresAtFromClaims(claims),
          },
        );
      } catch {
        // No session — leave store unauthenticated.
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signInToStore]);

  return ready;
}

function expiresAtFromClaims(claims: CognitoIdTokenClaims): number {
  const exp = (claims as { exp?: unknown }).exp;
  if (typeof exp === 'number' && Number.isFinite(exp)) {
    return exp * 1000;
  }
  // Cognito ID tokens default to 1 hour. Fall back so token refresh logic
  // still has a useful window instead of treating the token as immortal.
  return Date.now() + 3600 * 1000;
}

function normalizeRole(raw: unknown): UserRole {
  if (typeof raw !== 'string') return 'trainee';
  if (raw === 'trainee' || raw === 'instructor' || raw === 'org_admin' || raw === 'superadmin') {
    return raw;
  }
  return 'trainee';
}
